import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, MenuItem,
  Paper, Stack, TextField, Typography,
} from '@mui/material';
import ProaDatePicker from '../../../components/Common/ProaDatePicker';
// PRC-048: el panel común «Gestión del aviso» sustituye a «Iniciar investigación»
// y sus opciones de «Próxima acción», al selector «Siguiente acción».
import CadenceManagementDrawer from './cadenceManagement/CadenceManagementDrawer';
import NextActionChips from './cadenceManagement/NextActionChips';
import { cadenceDecisionAction } from './cadenceManagement/cadenceDecisionAction';
import { useDeferredCadenceActions } from './cadenceManagement/useDeferredCadenceActions';
import {
  addMarginCenterFollowUpNote,
  closeMarginCenterFollowUp,
  createMarginCenterFollowUp,
  getMarginCenterFollowUp,
  getMarginCenterFollowUpInventory,
  openOrLinkMarginCenterCadenceFollowUp,
  setMarginCenterFollowUpStatus,
  updateMarginCenterFollowUpPlanning,
} from '../services/marginCenterService';
import type {
  MarginCenterFollowUpCloseReason,
  MarginCenterFollowUpResponse,
  MarginCenterFollowUpPlanningBody,
} from '../types/marginCenter';

const closeReasons: Array<{ value: MarginCenterFollowUpCloseReason; label: string }> = [
  { value: 'explained', label: 'Situación explicada' },
  { value: 'not_relevant', label: 'No relevante' },
  { value: 'duplicate', label: 'Duplicado' },
  { value: 'discarded', label: 'Descartado' },
  { value: 'insufficient_evidence', label: 'Evidencia insuficiente' },
];

const eventLabel: Record<string, string> = {
  created: 'Seguimiento iniciado',
  note_added: 'Nota comercial',
  status_changed: 'Estado modificado',
  next_action_changed: 'Siguiente acción modificada',
  next_review_changed: 'Próxima revisión modificada',
  closed: 'Seguimiento cerrado',
};

function formatWhen(value: string | null | undefined): string {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
}

function reviewDateOnly(
  value: string | null | undefined,
): string {
  if (!value) return '';

  const match =
    /^(\d{4}-\d{2}-\d{2})/.exec(value);

  return match?.[1] ?? '';
}

function formatReviewDate(
  value: string | null | undefined,
): string {
  const dateOnly = reviewDateOnly(value);

  if (!dateOnly) {
    return 'Sin fecha';
  }

  const [year, month, day] =
    dateOnly.split('-').map(Number);

  const date = new Date(
    year,
    month - 1,
    day,
  );

  return Number.isNaN(date.getTime())
    ? dateOnly
    : date.toLocaleDateString(
      'es-ES',
      { dateStyle: 'medium' },
    );
}

function reviewDateToIso(
  value: string,
): string | null {
  return value
    ? `${value}T00:00:00.000Z`
    : null;
}

function operationErrorMessage(
  error: unknown,
): string {
  if (error == null) {
    return '';
  }

  if (typeof error === 'string') {
    return error.trim();
  }

  const candidate = error as {
    response?: {
      data?: {
        detail?: unknown;
      };
      status?: number;
    };
    message?: string;
  };

  const detail =
    candidate.response?.data?.detail;

  if (
    typeof detail === 'string'
    && detail.trim()
  ) {
    return detail.trim();
  }

  if (
    typeof candidate.message === 'string'
    && candidate.message.trim()
  ) {
    return candidate.message.trim();
  }

  return (
    'La operación no se ha podido completar. '
    + 'Revisa los datos e inténtalo de nuevo.'
  );
}

function statusLabel(value: unknown): string {
  if (value === 'new') return 'Nuevo';
  if (value === 'in_review') return 'En revisión';
  if (value === 'closed') return 'Cerrado';
  return 'Estado registrado';
}

function signalFamilyLabel(value: string): string {
  switch (value) {
    case 'cadence': return 'Cadencia de compra';
    case 'continuity': return 'Continuidad de compra';
    case 'consumption_rate': return 'Ritmo de consumo';
    case 'purchase_frequency': return 'Frecuencia de compra';
    case 'purchase_size': return 'Tamaño de compra';
    default: return 'Actividad comercial';
  }
}

export default function CustomerRelationshipFollowUpPanel({
  customerId,
  productId,
  initialFollowUpId,
  cadenceAttentionOccurrenceDate,
  customerLabel,
  productLabel,
  onFollowUpIdChange,
  onOpenInventory,
}: {
  customerId: string;
  productId: string;
  customerLabel?: string | null;
  productLabel?: string | null;
  initialFollowUpId?: string | null;
  cadenceAttentionOccurrenceDate?: string | null;
  onFollowUpIdChange?: (id: string) => void;
  onOpenInventory?: () => void;
}) {
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState(initialFollowUpId ?? null);
  const [managementOpen, setManagementOpen] = useState(false);
  const deferredActions = useDeferredCadenceActions();
  const pendingManagement = deferredActions.pending.length > 0;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  // DUX-02C: la actividad completa sigue disponible bajo demanda.
  const [openingReason, setOpeningReason] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [planningOpen, setPlanningOpen] = useState(false);
  const [plannedAction, setPlannedAction] = useState('');
  const [plannedReviewAt, setPlannedReviewAt] = useState('');
  const [originalReviewAt, setOriginalReviewAt] = useState<string | null>(null);
  const [reviewTouched, setReviewTouched] = useState(false);
  const [planningExpectedAt, setPlanningExpectedAt] = useState('');
  const [note, setNote] = useState('');
  const [closeFormOpen, setCloseFormOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<MarginCenterFollowUpCloseReason>('explained');
  const [resolution, setResolution] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [relatedProductId, setRelatedProductId] = useState('');
  const [success, setSuccess] = useState('');
  const [activeOperation, setActiveOperation] = useState<
    'create' | 'cadence' | 'note' | 'status' | 'planning' | 'close' | null
  >(null);

  useEffect(() => {
    setActiveId(initialFollowUpId ?? null);
    setCloseFormOpen(false);
    setPlanningOpen(false);
    setHistoryOpen(false);
    setNoteEditorOpen(false);
    setActiveOperation(null);
  }, [
    customerId,
    productId,
    initialFollowUpId,
    cadenceAttentionOccurrenceDate,
  ]);

  useEffect(() => {
    setSuccess('');
    setActiveOperation(null);
  }, [customerId, productId]);

  // DUX-03G: consulta directa de expedientes abiertos de la relación.
  // limit=2 + has_more distingue 0 / 1 / varios sin una muestra global.
  const relationshipInventoryQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'inventory',
      'relationship',
      customerId,
      productId,
      'open-direct',
      2,
    ],
    queryFn: () => getMarginCenterFollowUpInventory({
      context: 'relationship',
      customer_id: customerId,
      product_id: productId,
      status: 'open',
      scope: 'customer_product',
      match: 'direct',
      sort: 'updated_desc',
      limit: 2,
    }),
    enabled: !activeId,
  });

  const matchingOpenCases = relationshipInventoryQuery.data?.items ?? [];
  const hasAdditionalOpenCases = Boolean(
    relationshipInventoryQuery.data?.page.has_more,
  );
  const hasSeveralOpenCases = (
    matchingOpenCases.length > 1
    || hasAdditionalOpenCases
  );
  const uniqueExistingId = (
    matchingOpenCases.length === 1
    && !hasAdditionalOpenCases
  )
    ? matchingOpenCases[0].id
    : null;

  useEffect(() => {
    if (!activeId && uniqueExistingId) {
      setActiveId(uniqueExistingId);
      onFollowUpIdChange?.(uniqueExistingId);
    }
  }, [activeId, uniqueExistingId, onFollowUpIdChange]);

  const detailQuery = useQuery({
    queryKey: ['margin-center', 'follow-ups', 'detail', activeId],
    queryFn: () => getMarginCenterFollowUp(activeId!),
    enabled: Boolean(activeId),
    retry: 1,
  });

  const refresh = (result: MarginCenterFollowUpResponse) => {
    const id = result.follow_up?.id;
    if (id) {
      queryClient.setQueryData(['margin-center', 'follow-ups', 'detail', id], result);
      setActiveId(id);
      onFollowUpIdChange?.(id);
    }
    void queryClient.invalidateQueries({
      queryKey: ['margin-center', 'follow-ups', 'inventory'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['margin-center', 'follow-ups', 'attention'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['margin-center', 'follow-ups', 'portfolio'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['margin-center', 'cadence-attention'],
    });
    void queryClient.invalidateQueries({ queryKey: ['margin-center', 'cadence-radar'] });
  };

  const createMutation = useMutation({
    mutationFn: () => createMarginCenterFollowUp(customerId, productId, {
      opening_reason: openingReason.trim(),
      next_action: nextAction.trim() || null,
    }),
    onSuccess: (result) => {
      refresh(result);
      setOpeningReason('');
      setNextAction('');
      setActiveOperation(null);
      setSuccess(result.created === false ? 'Seguimiento existente recuperado.' : 'Seguimiento creado.');
    },
  });

  const cadenceFollowUpMutation = useMutation({
    mutationFn: (
      followUpId: string | null,
    ) => {
      if (!cadenceAttentionOccurrenceDate) {
        throw new Error(
          'No existe una ocurrencia de cadencia activa en este contexto.',
        );
      }

      return openOrLinkMarginCenterCadenceFollowUp(
        customerId,
        productId,
        {
          first_outside_p90_date:
            cadenceAttentionOccurrenceDate,
          follow_up_id:
            followUpId,
          opening_reason:
            followUpId
              ? null
              : openingReason.trim() || null,
          next_action:
            followUpId
              ? null
              : nextAction.trim() || null,
        },
      );
    },
    onSuccess: (result) => {
      refresh(result);
      setOpeningReason('');
      setNextAction('');
      setActiveOperation(null);

      setSuccess(
        result.cadence_attention_action === 'linked'
          ? 'Episodio de cadencia vinculado al Seguimiento.'
          : result.cadence_attention_action === 'already_linked'
            ? 'Este episodio ya estaba vinculado a un Seguimiento.'
            : 'Seguimiento creado y vinculado a este episodio de cadencia.',
      );
    },
  });

  const noteMutation = useMutation({
    mutationFn: () => addMarginCenterFollowUpNote(activeId!, { content: note.trim() }),
    onSuccess: (result) => { refresh(result); setNote(''); setActiveOperation(null); setSuccess('Nota guardada.'); },
  });
  const statusMutation = useMutation({
    mutationFn: (status: 'new' | 'in_review') => setMarginCenterFollowUpStatus(activeId!, { status }),
    onSuccess: (result) => { refresh(result); setActiveOperation(null); setSuccess('Estado actualizado.'); },
  });
  const planningMutation = useMutation({
    mutationFn: (body: MarginCenterFollowUpPlanningBody) =>
      updateMarginCenterFollowUpPlanning(activeId!, body),
    onSuccess: (result) => {
      refresh(result);
      setPlanningOpen(false);
      setActiveOperation(null);
      setSuccess(result.planning_change === 'unchanged'
        ? 'No había cambios de planificación.'
        : 'Planificación actualizada y registrada en la cronología.');
    },
  });
  const closeMutation = useMutation({
    mutationFn: () => closeMarginCenterFollowUp(activeId!, {
      close_reason: closeReason,
      resolution_type: resolution.trim() || null,
      conclusion: conclusion.trim() || null,
      related_product_id: relatedProductId.trim() || null,
    }),
    onSuccess: (result) => { refresh(result); setCloseFormOpen(false); setActiveOperation(null); setSuccess('Seguimiento cerrado; su memoria permanece disponible.'); },
  });

  const busy = createMutation.isPending || cadenceFollowUpMutation.isPending || noteMutation.isPending || statusMutation.isPending || planningMutation.isPending || closeMutation.isPending;
  // Only the most recently requested operation can show a mutation error.
  // A previous failure must never appear next to a later success.
  const error = activeOperation === 'create' ? createMutation.error
    : activeOperation === 'cadence' ? cadenceFollowUpMutation.error
    : activeOperation === 'note' ? noteMutation.error
    : activeOperation === 'status' ? statusMutation.error
    : activeOperation === 'planning' ? planningMutation.error
    : activeOperation === 'close' ? closeMutation.error : null;
  const errorMessage =
    operationErrorMessage(error);
  const beginOperation = (operation: 'create' | 'cadence' | 'note' | 'status' | 'planning' | 'close') => {
    setSuccess('');
    setActiveOperation(operation);
  };
  const followUp = detailQuery.data?.follow_up ?? null;
  const validContext = followUp?.customer_id === customerId && followUp?.product_id === productId;
  const isClosed = followUp?.status === 'closed';

  const currentCadenceOccurrenceLinked = Boolean(
    cadenceAttentionOccurrenceDate
    && detailQuery.data?.signal_links.some((link) =>
      link.signal_code === 'cadence_outside_historical_pattern'
      && link.signal_snapshot.first_outside_p90_date
        === cadenceAttentionOccurrenceDate),
  );
  const startPlanning = () => {
    if (!followUp || !validContext || isClosed) return;
    setPlannedAction(followUp.next_action ?? '');
    setOriginalReviewAt(followUp.next_review_at);
    setPlannedReviewAt(
      reviewDateOnly(
        followUp.next_review_at,
      ),
    );
    setReviewTouched(false);
    setPlanningExpectedAt(followUp.updated_at);
    setPlanningOpen(true);
    setActiveOperation(null);
    setSuccess('');
  };
  const savePlanning = () => {
    if (!planningOpen || busy || !planningExpectedAt) return;
    beginOperation('planning');
    planningMutation.mutate({
      expected_updated_at: planningExpectedAt,
      next_action: plannedAction.trim() || null,
      next_review_at: !reviewTouched
        ? originalReviewAt
        : reviewDateToIso(plannedReviewAt),
    });
  };
  const originSignal = detailQuery.data?.signal_links.find((item) => item.link_type === 'origin') ?? null;
  const openingEvent = detailQuery.data?.events.find((item) =>
    item.event_type === 'note_added' && item.payload.note_kind === 'opening_reason'
    && typeof item.payload.content === 'string'
  );
  const recordedOpeningReason = openingEvent && typeof openingEvent.payload.content === 'string'
    ? openingEvent.payload.content : null;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        minWidth: 0,
        borderRadius: 2.5,
        p: { xs: 1.5, md: 2.25 },
        border: '1px solid rgba(56,189,248,0.22)',
        bgcolor: 'rgba(15,23,42,0.65)',
      }}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>
              Investigación comercial
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Expediente humano vinculado a esta relación. No se infieren causas ni se crean casos automáticamente.
            </Typography>
          </Box>
          {followUp && validContext && (
            <Chip size="small" label={statusLabel(followUp.status)} color={isClosed ? 'default' : 'info'} />
          )}
        </Stack>

        {(relationshipInventoryQuery.isLoading || detailQuery.isLoading) && <CircularProgress size={18} />}
        {relationshipInventoryQuery.isError && !activeId && (
          <Alert severity="warning">
            No se pudieron consultar los Seguimientos abiertos de esta relación. La creación queda bloqueada hasta recuperar la información.
          </Alert>
        )}
        {detailQuery.isError && activeId && (
          <Alert severity="warning">No se pudo recuperar este Seguimiento. No se ha modificado.</Alert>
        )}
        {error && !busy && (
          <Alert severity="error">
            {errorMessage}
          </Alert>
        )}
        {error && activeOperation === 'planning' && !busy && (
          <Button size="small" variant="text" onClick={() => {
            setPlanningOpen(false);
            setActiveOperation(null);
            void detailQuery.refetch();
          }}>Recargar expediente antes de reintentar</Button>
        )}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

        {cadenceAttentionOccurrenceDate && (
          <Alert
            severity={currentCadenceOccurrenceLinked ? 'success' : 'info'}
          >
            {currentCadenceOccurrenceLinked
              ? (
                'Este episodio de ruptura de cadencia ya está vinculado '
                + 'a la memoria comercial de este Seguimiento.'
              )
              : (
                `Aviso automático de cadencia activo desde ${
                  cadenceAttentionOccurrenceDate
                }. Abrir esta relación no crea ni modifica un Seguimiento.`
              )}
          </Alert>
        )}

        {!activeId && uniqueExistingId && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Recuperando expediente…</Typography>
          </Stack>
        )}

        {!activeId && hasSeveralOpenCases && (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(2,6,23,0.3)', border: '1px solid rgba(148,163,184,0.18)' }}>
            <Stack spacing={1}>
              <Typography sx={{ color: '#e2e8f0', fontWeight: 800 }}>
                Hay varios seguimientos abiertos de esta relación
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Elige la investigación concreta antes de continuar. No se mezclan expedientes diferentes.
              </Typography>
              {!hasAdditionalOpenCases && matchingOpenCases.map((item) => (
                <Button
                  key={item.id}
                  variant="outlined"
                  size="small"
                  disabled={busy}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => { setActiveId(item.id); onFollowUpIdChange?.(item.id); }}
                >
                  {statusLabel(item.status)} · {item.next_action || 'Siguiente acción pendiente'}
                </Button>
              ))}
              {onOpenInventory && (
                <Button
                  variant={hasAdditionalOpenCases ? 'contained' : 'text'}
                  size="small"
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  onClick={onOpenInventory}
                >
                  Ver todos los expedientes de la relación
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {!activeId && matchingOpenCases.length === 0 && relationshipInventoryQuery.isSuccess && (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(2,6,23,0.3)', border: '1px solid rgba(148,163,184,0.18)' }}>
            <Stack spacing={1.5}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                {cadenceAttentionOccurrenceDate ? 'Gestión del aviso' : 'Seguimiento de la relación'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                {pendingManagement
                  ? 'Guardando la gestión… Puedes deshacerla desde el aviso de abajo a la derecha.'
                  : cadenceAttentionOccurrenceDate
                    ? 'Registra qué se hace con este aviso: archivarlo con su causa, abrir un seguimiento o descartarlo.'
                    : 'Abre un seguimiento de esta relación con su próxima acción y fecha de revisión.'}
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  disabled={busy || pendingManagement}
                  onClick={() => setManagementOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {cadenceAttentionOccurrenceDate ? 'Gestionar' : 'Abrir seguimiento'}
                </Button>
              </Box>
              <CadenceManagementDrawer
                open={managementOpen}
                target={{
                  customer_id: customerId,
                  customer_label: customerLabel || customerId,
                  product_id: productId,
                  product_label: productLabel || productId,
                  first_outside_p90_date: cadenceAttentionOccurrenceDate ?? null,
                  last_purchase_date: null,
                  days_since_last_purchase: null,
                  p90_interval_days: null,
                }}
                onClose={() => setManagementOpen(false)}
                onConfirm={(target, decision) => {
                  deferredActions.schedule(cadenceDecisionAction(target, decision, 'pending'));
                  setManagementOpen(false);
                }}
              />
            </Stack>
          </Paper>
        )}

        {activeId && followUp && !validContext && (
          <Alert severity="error">Este expediente no corresponde a la relación seleccionada. No se muestran acciones de modificación.</Alert>
        )}

        {activeId && followUp && validContext && (
          <>
            {cadenceAttentionOccurrenceDate
              && !currentCadenceOccurrenceLinked
              && !isClosed && (
              <Alert
                severity="info"
                action={(
                  <Button
                    color="inherit"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      beginOperation('cadence');
                      cadenceFollowUpMutation.mutate(activeId);
                    }}
                  >
                    Vincular episodio
                  </Button>
                )}
              >
                Este Seguimiento está abierto, pero todavía no consta
                vinculado a la ocurrencia actual de ruptura de cadencia.
                La vinculación requiere una acción explícita.
              </Alert>
            )}

            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Abierto: {formatWhen(followUp.created_at)} · Última actualización: {formatWhen(followUp.updated_at)}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0, 1.4fr) minmax(300px, 0.8fr)' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Stack spacing={2} sx={{ minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(2,6,23,0.30)', border: '1px solid rgba(148,163,184,0.16)' }}>
                  <Stack spacing={0.9}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>Situación investigada</Typography>
                    <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800, whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
                      {recordedOpeningReason || 'Motivo de apertura no registrado'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Motivo declarado por una persona al iniciar el expediente.</Typography>
                    <Divider sx={{ my: 0.75 }} />
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 750 }}>Origen de la investigación</Typography>
                    {originSignal ? (
                      <>
                        <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                          Seguimiento vinculado a una señal de {signalFamilyLabel(originSignal.signal_family).toLowerCase()}.
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          Evaluada el {originSignal.evaluated_at}. La señal es una observación, no una causa ni una conclusión comercial.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ color: '#e2e8f0' }}>Investigación iniciada manualmente para esta relación cliente-producto.</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          No consta una señal de origen vinculada. La apertura es una declaración humana; no se deduce de las ventas actuales.
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(2,6,23,0.30)', border: '1px solid rgba(148,163,184,0.16)' }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>Actividad e historial</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Chip size="small" variant="outlined" label={`${detailQuery.data?.events.length ?? 0} eventos`} />
                        <Button size="small" variant="text" onClick={() => setHistoryOpen((value) => !value)}>
                          {historyOpen ? 'Ocultar historial' : 'Ver historial'}
                        </Button>
                      </Stack>
                    </Stack>
                    <Collapse in={historyOpen} timeout="auto" unmountOnExit>
                      <Stack spacing={1}>
                    {(detailQuery.data?.events ?? []).length === 0 && (
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Todavía no hay eventos registrados.</Typography>
                    )}
                    <Stack spacing={0.9} sx={{ maxHeight: 330, overflowY: 'auto', pr: 0.5 }}>
                      {(detailQuery.data?.events ?? []).map((event) => (
                        <Box key={event.id} sx={{ p: 1.25, borderLeft: '2px solid rgba(56,189,248,0.36)', bgcolor: 'rgba(15,23,42,0.58)', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 750 }}>
                            {event.event_type === 'note_added' && event.payload.note_kind === 'opening_reason'
                              ? 'Motivo de apertura' : eventLabel[event.event_type] ?? 'Evento del expediente'} · {formatWhen(event.created_at)}
                          </Typography>
                          {event.event_type === 'note_added' && typeof event.payload.content === 'string' && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#f8fafc', whiteSpace: 'pre-wrap' }}>{event.payload.content}</Typography>
                          )}
                          {event.event_type === 'status_changed' && (
                            <Typography variant="body2" sx={{ mt: 0.35, color: '#e2e8f0' }}>
                              {statusLabel(event.payload.previous_status)} → {statusLabel(event.payload.new_status)}
                            </Typography>
                          )}
                          {event.event_type === 'next_action_changed' && (
                            <Typography variant="body2" sx={{ mt: 0.35, color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                              Antes: {typeof event.payload.previous_action === 'string' ? event.payload.previous_action : 'Sin definir'}
                              {' · '}Ahora: {typeof event.payload.new_action === 'string' ? event.payload.new_action : 'Sin definir'}
                            </Typography>
                          )}
                          {event.event_type === 'next_review_changed' && (
                            <Typography variant="body2" sx={{ mt: 0.35, color: '#e2e8f0' }}>
                              Antes: {typeof event.payload.previous_review_at === 'string' ? formatReviewDate(event.payload.previous_review_at) : 'No programada'}
                              {' · '}Ahora: {typeof event.payload.new_review_at === 'string' ? formatReviewDate(event.payload.new_review_at) : 'No programada'}
                            </Typography>
                          )}
                          {event.event_type === 'closed' && typeof event.payload.conclusion === 'string' && (
                            <Typography variant="body2" sx={{ mt: 0.35, color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>{event.payload.conclusion}</Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                      </Stack>
                    </Collapse>
                    {!isClosed && (
                      <>
                        <Divider />
                        <Box>
                          <Button size="small" variant="outlined" onClick={() => setNoteEditorOpen((value) => !value)}>
                            {noteEditorOpen ? 'Ocultar editor' : 'Añadir nota comercial'}
                          </Button>
                        </Box>
                        <Collapse in={noteEditorOpen} timeout="auto">
                          <Stack spacing={1}>
                            <TextField fullWidth size="small" label="Nota comercial" multiline minRows={3} value={note} inputProps={{ maxLength: 10000 }} onChange={(event) => setNote(event.target.value)} />
                            <Box>
                              <Button size="small" variant="outlined" disabled={busy || !note.trim()} onClick={() => { beginOperation('note'); noteMutation.mutate(); }}>Guardar nota</Button>
                            </Box>
                          </Stack>
                        </Collapse>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Stack>

              <Stack spacing={2} sx={{ minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(2,6,23,0.30)', border: '1px solid rgba(148,163,184,0.16)' }}>
                  <Stack spacing={1.15}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>{isClosed ? 'Estado final del expediente' : 'Qué necesita atención ahora'}</Typography>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>Siguiente acción</Typography>
                      <Typography variant="body1" sx={{ color: followUp.next_action ? '#f8fafc' : '#fbbf24', fontWeight: 750, whiteSpace: 'pre-wrap' }}>{followUp.next_action || 'Pendiente de definir'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>Próxima revisión</Typography>
                      <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 650 }}>{followUp.next_review_at ? formatReviewDate(followUp.next_review_at) : 'No programada'}</Typography>
                    </Box>
                    {!isClosed && (
                      <>
                        <Divider />
                        {!planningOpen ? (
                          <Button size="small" variant="outlined" disabled={busy} onClick={startPlanning}>
                            {followUp.next_action || followUp.next_review_at ? 'Editar planificación' : 'Definir siguiente acción y revisión'}
                          </Button>
                        ) : (
                          <Stack spacing={1}>
                            <NextActionChips
                              value={plannedAction}
                              onChange={setPlannedAction}
                              disabled={busy}
                            />
                            <ProaDatePicker
                              label="Próxima revisión"
                              value={plannedReviewAt}
                              disabled={busy}
                              onChange={(value) => {
                                setPlannedReviewAt(value);
                                setReviewTouched(true);
                              }}
                              helperText="Selecciona el día de revisión. No se programa una hora."
                            />
                            <Button size="small" variant="text" disabled={busy}
                              onClick={() => { setPlannedReviewAt(''); setReviewTouched(true); }}>
                              Quitar fecha de revisión
                            </Button>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              <Button size="small" variant="contained" disabled={busy || !planningExpectedAt}
                                onClick={savePlanning}>Guardar planificación</Button>
                              <Button size="small" variant="text" disabled={busy}
                                onClick={() => { setPlanningOpen(false); setActiveOperation(null); }}>
                                Cancelar
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                        {followUp.status === 'new' && (
                          <Button size="small" variant="outlined" disabled={busy} onClick={() => { beginOperation('status'); statusMutation.mutate('in_review'); }}>Pasar a revisión</Button>
                        )}
                        <Button size="small" color="inherit" variant="outlined" disabled={busy} onClick={() => setCloseFormOpen((value) => !value)}>
                          {closeFormOpen ? 'Cancelar cierre' : 'Preparar cierre'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Paper>

                {isClosed && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(16,46,37,0.34)', border: '1px solid rgba(52,211,153,0.22)' }}>
                    <Stack spacing={0.75}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>Resolución documentada</Typography>
                      <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Motivo: {closeReasons.find((item) => item.value === followUp.close_reason)?.label ?? 'No disponible'}</Typography>
                      {followUp.resolution_type && <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Tipo: {followUp.resolution_type}</Typography>}
                      {followUp.related_product_id && <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Producto relacionado: {followUp.related_product_id}</Typography>}
                      {followUp.conclusion && <Typography variant="body2" sx={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>Conclusión humana: {followUp.conclusion}</Typography>}
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>Cerrado: {formatWhen(followUp.closed_at)}</Typography>
                    </Stack>
                  </Paper>
                )}

                {!isClosed && closeFormOpen && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(15,23,42,0.75)', border: '1px solid rgba(251,191,36,0.24)' }}>
                    <Stack spacing={1.15}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>Documentar resolución</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>El cierre conserva el historial. La conclusión expresa juicio humano, no una inferencia automática.</Typography>
                      <TextField select size="small" label="Motivo de cierre" value={closeReason} onChange={(event) => setCloseReason(event.target.value as MarginCenterFollowUpCloseReason)}>
                        {closeReasons.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                      </TextField>
                      <TextField size="small" label="Tipo de resolución (opcional)" placeholder="Ej.: Sustitución" value={resolution} onChange={(event) => setResolution(event.target.value)} inputProps={{ maxLength: 120 }} />
                      <TextField size="small" label="Producto relacionado (ID, opcional)" value={relatedProductId} onChange={(event) => setRelatedProductId(event.target.value)} />
                      <TextField size="small" label="Conclusión humana" multiline minRows={3} value={conclusion} onChange={(event) => setConclusion(event.target.value)} />
                      <Button color="warning" variant="outlined" disabled={busy || (closeReason === 'explained' && !conclusion.trim())} onClick={() => { beginOperation('close'); closeMutation.mutate(); }}>Confirmar cierre</Button>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
